import AntdCard from 'antd/lib/card'
import styles from './FullWidthCard.module.less'
import { ReactElement, useMemo, useState } from 'react'
import CopyButton from '../../CopyButton/CopyButton'
import chevronDownIcon from 'Assets/icons/chevron-down.svg'
import { TaskStatsIcon } from 'Components/common/Icons/TaskStatsIcon'
import Flex from 'antd/lib/flex'
export type CardTagPropsType = {
    title: string
    type: 'primary' | 'maker' | 'reviewer' | 'qc' | 'triager' | 'rework'
}

export type CardPropsType = {
    title: string
    tags?: CardTagPropsType[]
    content?: string
    footer?: ReactElement
    onClick: () => void
    maxContentWords?: number
    extra?: ReactElement
    stepId?: string
    onCopyStepId?: (stepId: string) => void
    onAccordionToggle?: () => void
    onClickCopy?: () => void
}

const FullWidthCard = (props: CardPropsType) => {
    const [showingMore, setShowingMore] = useState(false)
    const [isAccordionOpen, setIsAccordionOpen] = useState(false)
    const totalWords = useMemo(() => props.content?.split(' ').length ?? 0, [props.content])
    const content = useMemo(() => {
        if (showingMore || totalWords <= (props.maxContentWords ?? 0)) {
            return props.content
        }
        return props.content?.split(' ').slice(0, props.maxContentWords).join(' ') + '...'
    }, [props.content, props.maxContentWords, showingMore, totalWords])

    const footerContentId = useMemo(
        () => (props.stepId ? `card-footer-content-${props.stepId}` : 'card-footer-content'),
        [props.stepId]
    )

    const toggleAccordion = () => {
        !isAccordionOpen && props.onAccordionToggle?.()
        setIsAccordionOpen(!isAccordionOpen)
    }

    const handleAccordionToggle = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        toggleAccordion()
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            e.stopPropagation()
            toggleAccordion()
        }
    }

    return (
        <div className={styles['card-container']} data-testid="full-width-card-wrapper">
            <AntdCard
                title={
                    <Flex justify={'space-between'}>
                        <div>{props.title}</div>
                        <div className={styles['copy-button-container']}>
                            {props.stepId && (
                                <CopyButton
                                    onClick={() => props.onCopyStepId?.(props.stepId!)}
                                    showTooltip
                                    tooltipTitle={`Copy ID: ${props.stepId}`}
                                    contentToCopy={props.stepId}
                                    copySuccessMessage="Task ID copied"
                                />
                            )}
                        </div>
                    </Flex>
                }
                className={styles['card'] + ' ' + styles['fullWidth']}
                classNames={{
                    header: styles['card-header'],
                    title: styles['card-title'],
                    body: styles['card-body'],
                }}
                extra={props.extra}
                onClick={() => props.onClick()}
            >
                {props.tags && props.tags.length > 0 && (
                    <div className={styles['tags-container']}>
                        {props.tags.map((tag: CardTagPropsType) => (
                            <div className={styles['tag'] + ' ' + styles[tag.type]} key={tag.title}>
                                {tag.title}
                            </div>
                        ))}
                    </div>
                )}
                {props.content && (
                    <div className={styles['description-container']}>
                        {content}
                        {props.maxContentWords && totalWords > 10 && (
                            <>
                                &nbsp;
                                <button
                                    onClick={e => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        setShowingMore(prev => !prev)
                                    }}
                                    className={styles['show-more-less']}
                                >
                                    Show {showingMore ? 'less' : 'more'}
                                </button>
                            </>
                        )}
                    </div>
                )}
                {props.footer && (
                    <div className={styles['footer']}>
                        <div
                            className={styles['footer-header']}
                            onClick={handleAccordionToggle}
                            role="button"
                            tabIndex={0}
                            aria-expanded={isAccordionOpen}
                            aria-controls={footerContentId}
                            onKeyDown={handleKeyDown}
                        >
                            <div className={styles['footer-left']}>
                                <TaskStatsIcon />
                                <span>Task Stats</span>
                            </div>
                            <img
                                src={chevronDownIcon}
                                alt={isAccordionOpen ? 'Close' : 'Open'}
                                className={`${styles['accordion-button']} ${
                                    isAccordionOpen ? styles['open'] : ''
                                }`}
                            />
                        </div>
                        {isAccordionOpen && (
                            <div className={styles['footer-content-inline']} id={footerContentId}>
                                {props.footer}
                            </div>
                        )}
                    </div>
                )}
            </AntdCard>
        </div>
    )
}

export default FullWidthCard
